import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  AuditDetail,
  DefaultCollateralIdentifierType,
  EntityTypeEnum,
  NegotiationHistorySubscriptionIdentifier,
  PlatformFeature,
  SubscriptionIdentifier
} from 'api'
import { CenterPanel } from 'view/routes/routesLayout'
import { EnvironmentTitle, HeroPanel } from 'view/shared/heroPanel'
import { DataGrid, filteredColumnDefsBuilder, GridId } from 'view/components/datagrid'
import { auditGridColumnDefs, auditGridOptions } from './AuditGrid/AuditGridModel'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { NegotiationSubscriptionFilters } from 'view/components/datagrid/filters/NegotiationSubscriptionFilters'
import { createAuditDetailsSelector } from 'selectors/auditSelectors'
import { isCurrentOrganisationBuySideSelector } from 'selectors/organisationSelectors'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { createEntitySubscriptionSelector } from 'selectors/selectionSelectors'
import { Locale, Translation } from 'locale'
import { WindowActions } from 'epics/windowEpic'
import { SelectionActions } from 'store/selection'
import { AuditActions } from 'api/AuditActions'
import { AuditGridColumnId } from './AuditGrid/AuditGridColumnNames'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { GridApi } from 'ag-grid-community'
import { defaultCollateralIdentifierTypeSelector } from 'selectors/userPreferenceSelectors'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { AdminOnlyColumnIds } from 'view/routes/ColumnNames'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'

interface AuditScreenProps {
  auditList: AuditDetail[]
  subscriptionDetail?: SubscriptionDetail
  isBuySideOrganisation: boolean
  allowPartialEnabled: boolean
  defaultCollateralIdentifierType: DefaultCollateralIdentifierType
  onTearout: () => void
  onPageSubscribe: typeof AuditActions.pageSubscribe
  onAddAuditSubscriptions: (orderIds: string[]) => unknown
  onRemoveAuditSubscriptions: (orderIds: string[]) => unknown
  onClearAuditSubscriptions: () => unknown
  isAdminUser: boolean
}

const mapStateToProps = createStructuredSelector({
  auditList: createAuditDetailsSelector(),
  subscriptionDetail: createEntitySubscriptionSelector(
    () => EntityTypeEnum.NEGOTIATION_HISTORY,
    () => SubscriptionIdentifier.LATEST
  ),
  isBuySideOrganisation: isCurrentOrganisationBuySideSelector,
  allowPartialEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ALLOW_PARTIAL),
  defaultCollateralIdentifierType: defaultCollateralIdentifierTypeSelector,
  isAdminUser: isAdminGatewayUserSelector
})

const mapDispatchToProps = {
  onTearout: () => WindowActions.tearoutGrid('/quote/tearout/history'),
  onPageSubscribe: AuditActions.pageSubscribe,
  onAddAuditSubscriptions: SelectionActions.addAuditSubscriptions,
  onRemoveAuditSubscriptions: SelectionActions.removeAuditSubscriptions,
  onClearAuditSubscriptions: SelectionActions.clearAuditSubscriptions
}

export const AuditScreenComponent: FunctionComponent<AuditScreenProps> = props => {
  const {
    auditList,
    subscriptionDetail,
    isBuySideOrganisation,
    allowPartialEnabled,
    defaultCollateralIdentifierType,
    onTearout,
    onPageSubscribe,
    onAddAuditSubscriptions,
    onRemoveAuditSubscriptions,
    onClearAuditSubscriptions,
    isAdminUser
  } = props

  const onChangeSubscription = useChangedSubscriptionFilters(EntityTypeEnum.NEGOTIATION_HISTORY, SubscriptionIdentifier.LATEST, true)

  useEffect(() => {
    onChangeSubscription(null)
    return () => {
      onChangeSubscription([])
    }
  }, [onChangeSubscription])

  const onPageChanged = (pageNumber: number) => {
    if (subscriptionDetail) {
      onPageSubscribe(SubscriptionIdentifier.LATEST, subscriptionDetail.filters, pageNumber)
    }
  }

  const handleRowGroupOpened = useCallback(
    (auditDetail: AuditDetail | undefined, expanded: boolean) => {
      if (!auditDetail) {
        return
      }
      if (expanded) {
        onAddAuditSubscriptions([auditDetail.negotiation.parentOrderId])
      } else {
        onRemoveAuditSubscriptions([auditDetail.negotiation.parentOrderId])
      }
    },
    [onAddAuditSubscriptions, onRemoveAuditSubscriptions]
  )

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    gridApi.setColumnsVisible(
      [AuditGridColumnId.COLLATERAL_ISIN_OR_BUI],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible(
      [AuditGridColumnId.COLLATERAL_CUSIP],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
    )
  }

  const filteredColumnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(auditGridColumnDefs(isBuySideOrganisation))
        .withoutIf(!isAdminUser, Object.values(AdminOnlyColumnIds))
        .withoutIf(!allowPartialEnabled, [AuditGridColumnId.ALLOW_PARTIAL])
        .build(),
    [allowPartialEnabled, isAdminUser, isBuySideOrganisation]
  )

  useEffect(() => {
    return () => {
      onClearAuditSubscriptions()
    }
  }, [onClearAuditSubscriptions])

  return (
    <CenterPanel>
      <HeroPanel onTearout={onTearout}>
        <EnvironmentTitle>{Locale.translate(Translation.TC_HERO_NEGOTIATION_HISTORY)}</EnvironmentTitle>
      </HeroPanel>
      <DataGrid
        gridId={GridId.AuditGrid}
        gridClass="negotiation-history-grid"
        gridOptions={auditGridOptions}
        columnDefs={filteredColumnDefs}
        rowData={auditList}
        disableGrouping={true}
        disableExport={true}
        customToolbarSwitches={[OrganisationFilterSwitch]}
        subscriptionFilters={gridApi => (
          <NegotiationSubscriptionFilters
            entityType={EntityTypeEnum.NEGOTIATION_HISTORY}
            subscriptionIdentifier={NegotiationHistorySubscriptionIdentifier.LATEST}
            pageResults={true}
            gridApi={gridApi}
          />
        )}
        subscriptionDetail={subscriptionDetail}
        onPageChanged={onPageChanged}
        onRowGroupOpened={handleRowGroupOpened}
        onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
      />
    </CenterPanel>
  )
}

export const AuditScreen = connect(mapStateToProps, mapDispatchToProps)(AuditScreenComponent)
